// Catch-weight maths for PopCart Fresh: pure and shared by the API (authoritative), the customer
// app (display) and the packing station (live recalculation). Integer centavos and grams only.
//
// Policy (docs/PHASE3-CONTRACT.md, Vol 2 "Sold by the gram, fairly"):
// - The customer agrees to an estimate: band midpoint (or the target weight) × price per kg, plus
//   any prep fee, per unit.
// - At packing the line is re-priced on the actual weight, but never above estimate + tolerance
//   (default 10%); above that the shop absorbs the difference. Below the estimate, the customer
//   pays less.
// - COD: the cash due simply becomes the final total. E-wallets/cards are charged the estimate
//   plus walletHoldPct on the weight lines up front, so settlement is always a refund (never a
//   top-up request that could fail).

/** Price for `grams` at `pricePerKgCentavos`, rounded to the centavo. */
export function priceForGrams(grams, pricePerKgCentavos) {
  if (!(grams >= 0) || !(pricePerKgCentavos >= 0)) throw new RangeError('grams and price must be ≥ 0');
  return Math.round((grams * pricePerKgCentavos) / 1000);
}

/** The weight a band promises per unit: its midpoint, in whole grams. */
export function bandEstimateGrams(band) {
  if (!band || !(band.minG > 0) || !(band.maxG >= band.minG)) throw new RangeError('invalid weight band');
  return Math.round((band.minG + band.maxG) / 2);
}

/**
 * The estimate for one cart line of a weight-priced product.
 * @param {{ pricePerKgCentavos: number, qty: number, band?: {minG:number,maxG:number}, targetG?: number, prepFeeCentavos?: number }} l
 * @returns {{ estGramsPerUnit: number, estLineCentavos: number }}
 */
export function estimateLine({ pricePerKgCentavos, qty, band, targetG, prepFeeCentavos = 0 }) {
  if (!Number.isInteger(qty) || qty < 1) throw new RangeError('qty must be a positive integer');
  const g = band ? bandEstimateGrams(band) : targetG;
  if (!Number.isInteger(g) || g <= 0) throw new RangeError('a band or a positive targetG is required');
  return { estGramsPerUnit: g, estLineCentavos: qty * (priceForGrams(g, pricePerKgCentavos) + prepFeeCentavos) };
}

/**
 * Re-price a line on its actual (total) weight at packing.
 * @returns {{ rawCentavos: number, finalCentavos: number, capCentavos: number, shopAbsorbedCentavos: number, deltaCentavos: number }}
 *   deltaCentavos = final − estimate (negative = the customer pays less).
 */
export function finalLine({ pricePerKgCentavos, qty, actualGrams, prepFeeCentavos = 0, estLineCentavos, tolerancePct = 10 }) {
  if (!Number.isInteger(actualGrams) || actualGrams <= 0) throw new RangeError('actualGrams must be a positive integer');
  if (!Number.isInteger(estLineCentavos) || estLineCentavos < 0) throw new RangeError('estLineCentavos is required');
  const raw = priceForGrams(actualGrams, pricePerKgCentavos) + qty * prepFeeCentavos;
  const cap = Math.floor((estLineCentavos * (100 + tolerancePct)) / 100);
  const final = Math.min(raw, cap);
  return { rawCentavos: raw, finalCentavos: final, capCentavos: cap, shopAbsorbedCentavos: raw - final, deltaCentavos: final - estLineCentavos };
}

/** What an e-wallet/card is charged up front: the estimate plus the hold on weight lines. */
export function walletCharge({ estTotalCentavos, estWeightSubtotalCentavos, walletHoldPct = 10 }) {
  return estTotalCentavos + Math.ceil((estWeightSubtotalCentavos * walletHoldPct) / 100);
}

/**
 * Settlement once every weight line is weighed.
 * @param {{ method: 'cod'|'gcash'|'maya'|'card', estTotalCentavos: number, chargedCentavos?: number, finalTotalCentavos: number }} s
 * @returns {{ codDueCentavos: number|null, refundCentavos: number, adjustmentCentavos: number }}
 */
export function settle({ method, estTotalCentavos, chargedCentavos, finalTotalCentavos }) {
  if (method === 'cod') {
    return { codDueCentavos: finalTotalCentavos, refundCentavos: 0, adjustmentCentavos: finalTotalCentavos - estTotalCentavos };
  }
  const charged = chargedCentavos ?? estTotalCentavos;
  return { codDueCentavos: null, refundCentavos: Math.max(0, charged - finalTotalCentavos), adjustmentCentavos: finalTotalCentavos - charged };
}
